import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import landingApi from "../../contexts/landingApiClient";
import ManagePayoutModal from "./ManagePayoutModal";
import ConfirmationModal from "../CustomComponents/ConfirmationModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TablePagination,
  Skeleton,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import formatCurrency from "../../utils/formatCurrency";

const getStatusChipColor = (status) => {
  switch (status) {
    case "paid":
      return "success";
    case "processing":
      return "info";
    case "failed":
    case "rejected":
      return "error";
    case "draft":
      return "secondary";
    default:
      return "default";
  }
};

const PayoutsListTable = ({
  affiliateId,
  pageSize: initialPageSize = 5,
  modalOpen,
  handleCloseModal,
  onPayoutCreated,
  onPayoutDeleted,
  onEditPayout,
  payoutToEdit,
  availableBalance,
  affiliate,
}) => {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [payoutToDelete, setPayoutToDelete] = useState(null);

  const fetchPayouts = useCallback(async () => {
    if (!affiliateId) return;
    setIsFetching(true);
    try {
      const response = await landingApi.get("/payouts", {
        params: {
          "filters[affiliate][id][$eq]": affiliateId,
          "pagination[page]": page + 1,
          "pagination[pageSize]": rowsPerPage,
          populate: ["affiliate"],
          sort: "updatedAt:desc",
        },
      });
      setPayouts(response.data.data);
      if (response.data.meta && response.data.meta.pagination) {
        setPagination(response.data.meta.pagination);
      }
    } catch (err) {
      console.error(t("error_fetch_payouts"), err);
      setError(t("error_fetch_payouts"));
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [affiliateId, page, rowsPerPage]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleLocalPayoutCreated = () => {
    if (onPayoutCreated) {
      onPayoutCreated();
    }
    fetchPayouts();
  };

  const handleMenuClick = (event, payout) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayout(payout);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayout(null);
  };

  const handleEdit = () => {
    onEditPayout(selectedPayout);
    handleMenuClose();
  };

  const handleDelete = () => {
    setPayoutToDelete(selectedPayout);
    setIsDeleteModalOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!payoutToDelete) return;
    try {
      await landingApi.delete(`/payouts/${payoutToDelete.id}`);
      toast.success(t("toast_payout_deleted_success"));
      fetchPayouts(); // Refresh the payouts list
      if (onPayoutDeleted) {
        onPayoutDeleted(); // Callback to refresh parent component data
      }
    } catch (error) {
      console.error("Error deleting payout:", error);
      toast.error(t("toast_payout_deleted_error"));
    } finally {
      setIsDeleteModalOpen(false);
      setPayoutToDelete(null);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSkeleton = loading && payouts.length === 0;

  return (
    <Paper sx={{ p: 3, position: "relative", mt: 3 }}>
      <ToastContainer />
      <Typography variant="h6" gutterBottom>
        {t("payouts")}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {isFetching && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {modalOpen && (
            <ManagePayoutModal
              open={modalOpen}
              handleClose={handleCloseModal}
              onPayoutCreated={handleLocalPayoutCreated}
              payout={payoutToEdit}
              availableBalance={availableBalance}
              affiliate={affiliate}
            />
          )}
          <ConfirmationModal
            open={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={handleConfirmDelete}
            title={t("confirm_payout_deletion_title")}
            description={t("confirm_payout_deletion_description")}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("created")}</TableCell>
                  <TableCell>{t("updated")}</TableCell>
                  <TableCell>{t("status")}</TableCell>
                  <TableCell>{t("last_status")}</TableCell>
                  <TableCell>{t("amount_with_currency")}</TableCell>
                  <TableCell>{t("notes")}</TableCell>
                  <TableCell>{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showSkeleton ? (
                  Array.from(new Array(rowsPerPage)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))
                ) : payouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t("no_payouts_found")}
                    </TableCell>
                  </TableRow>
                ) : (
                  payouts.map((payout) => (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        {moment(payout.attributes.createdAt).isValid()
                          ? moment(payout.attributes.createdAt).format(
                              "DD MMM YYYY"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {moment(payout.attributes.updatedAt).isValid()
                          ? moment(payout.attributes.updatedAt).format(
                              "DD MMM YYYY"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t('status_' + payout.attributes.status, { defaultValue: payout.attributes.status })}
                          color={getStatusChipColor(payout.attributes.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {payout.attributes.lastStatus ? (
                          <Chip
                            label={t('status_' + payout.attributes.lastStatus, { defaultValue: payout.attributes.lastStatus })}
                            color={getStatusChipColor(payout.attributes.lastStatus)}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(payout.attributes.amount)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={payout.attributes.notes || ""}>
                          <Typography variant="body2" noWrap>
                            {payout.attributes.notes || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuClick(event, payout)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={t("rows_per_page")}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEdit}>{t("edit")}</MenuItem>
            <MenuItem
              onClick={handleDelete}
              disabled={selectedPayout?.attributes?.status === "paid"}
            >
              {t("delete")}
            </MenuItem>
          </Menu>
        </>
      )}
    </Paper>
  );
};

export default PayoutsListTable;
